import React, { useEffect, useState } from 'react'
import { Typography, FormControl } from '@mui/material'
import { cn } from '@src/utils/cn.ts'
import styles from '../Poll.module.scss'
import { PollComponentProps } from './PollComponentType'
import LxCancelButton from '@src/components/cancelButton/LxCancelButton'
import LxSaveButton from '@src/components/saveButton/LxSaveButton'
import { normalizeOptional } from '../Poll'

const mapProperties = (properties: any) => {
  return {
    ...properties,
    choiceRange: {
      min: Number(properties.choice_range?.min ?? 1),
      max: Number(properties.choice_range?.max ?? 1),
    },
    choices: properties.choices ?? [],
    selectedStyle: properties.selected_style ?? {},
    unselectedStyle: properties.unselected_style ?? {},
    btnOk: properties.btn_ok || 'Ok',
    btnSkip: properties.btn_skip || 'Skip',
    distribution: properties.distribution || 'vertical',
  }
}

export const PollMultichoice: React.FC<PollComponentProps> = ({ data, responseCallback }) => {
  const [submitted, setSubmitted] = useState(false)
  const [canSubmit, setCanSubmit] = useState(false)
  const [isOptional, setIsOptional] = useState(false)
  const [userChoices, setUserChoices] = useState<Set<string>>(new Set())
  const [isResetting, setIsResetting] = useState(false)

  const props = mapProperties(data.properties)

  const maxChoices = props.choiceRange.max
  const minChoices = props.choiceRange.min
  const isMultiAnswer = maxChoices > 1

  const onSubmit = (skipped: boolean) => {
    setSubmitted(true)
    responseCallback({
      skipped,
      answer: { choices: [...userChoices] },
    })
  }

  const getDistributionStyle = () => {
    const distribution = props.webDistribution || props.distribution
    const numChoices = props.choices.length

    switch (distribution) {
      case 'horizontal':
        return {
          display: 'flex',
          flexDirection: 'row' as const,
          flexWrap: 'wrap' as const,
          justifyContent: 'center',
          gap: '8px',
          alignItems: 'center',
        }

      case 'grid':
        const numColumns = Math.ceil(numChoices / Math.sqrt(numChoices))
        return {
          display: 'grid',
          gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
          gap: '8px',
          justifyContent: 'center',
          width: '100%',
        }

      case 'vertical':
      default:
        return {
          display: 'flex',
          flexDirection: 'column' as const,
          alignItems: 'center',
          gap: '8px',
          width: '100%',
        }
    }
  }

  const getImageDistributionStyle = () => {
    const distribution = props.distribution
    const numChoices = props.choices.length

    switch (distribution) {
      case 'horizontal':
        return {
          display: 'flex',
          flexDirection: 'row' as const,
          flexWrap: 'wrap' as const,
          justifyContent: 'center',
          gap: '12px',
        }

      case 'grid':
        const numColumns = Math.ceil(numChoices / Math.sqrt(numChoices))
        return {
          display: 'grid',
          gridTemplateColumns: `repeat(${numColumns}, minmax(200px, 1fr))`,
          gap: '12px',
          width: '100%',

          '@media (max-width: 768px)': {
            gridTemplateColumns: `repeat(${Math.min(numColumns, 2)}, minmax(150px, 1fr))`,
          },

          '@media (max-width: 480px)': {
            gridTemplateColumns: `repeat(1, minmax(120px, 1fr))`,
          },
        }

      case 'vertical':
      default:
        return {
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px',
          width: '100%',
        }
    }
  }

  const onChoiceSelect = (choice: string) => {
    setUserChoices((prev) => {
      const updated = new Set(prev)
      if (updated.has(choice)) {
        updated.delete(choice)
      } else {
        if (!isMultiAnswer || updated.size < maxChoices) {
          updated.add(choice)
        }
      }
      return updated
    })
  }

  useEffect(() => {
    setIsResetting(true)
    setUserChoices(new Set())
    setSubmitted(false)
    setCanSubmit(false)
    setIsOptional(normalizeOptional(data?.optional))
    setTimeout(() => setIsResetting(false), 0)
  }, [data])

  useEffect(() => {
    if (isResetting || submitted) return
    if (!isMultiAnswer && userChoices.size > 0) {
      onSubmit(false)
    }
    setCanSubmit(isMultiAnswer && userChoices.size >= minChoices)
  }, [userChoices, isMultiAnswer, minChoices, submitted, isResetting])

  const getTextLengthClass = (text: string) => {
    const length = text.length
    if (length <= 15) return 'shortText'
    if (length <= 40) return 'mediumText'
    return 'longText'
  }

  const getButtonStyle = (isSelected: boolean, distribution: string) => {
    const baseStyle = isSelected ? props.selectedStyle.button : props.unselectedStyle.button

    // Distribution-specific adjustments
    switch (distribution) {
      case 'vertical':
        return {
          ...baseStyle,
          width: '100%',
          maxWidth: '300px',
          minWidth: 'unset',
        }

      case 'horizontal':
        return {
          ...baseStyle,
          minWidth: '100px',
          maxWidth: 'fit-content',
          flex: '1 1 auto',
        }

      case 'grid':
        return {
          ...baseStyle,
          width: '100%',
          minWidth: 'unset',
        }

      default:
        return {
          ...baseStyle,
          width: '100%',
          maxWidth: '300px',
          minWidth: 'unset',
        }
    }
  }

  return (
    <div
      className={styles.multiChoiceContainer}
      style={{
        overflow: 'auto',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
      key={data.id}
    >
      <div style={{ flex: '1 1 auto', overflow: 'auto' }}>
        <div className={styles.multiChoiceQuestion}>
          <Typography variant='h6' component='h6' sx={{ marginBottom: props.hint ? '20px' : '10px' }}>
            {data.showQuestionNumber ? `[${data.id}] ${props.question}` : props.question}
          </Typography>
          {props.hint && (
            <Typography variant='body1' className={styles.hint}>
              {props.hint}
            </Typography>
          )}
        </div>

        {/* 🔹 Button-type input */}
        {props.input_type === 'button' && (
          <FormControl fullWidth>
            <div className={cn(styles.multiChoiceButtonContainer)} role='listbox' aria-multiselectable={isMultiAnswer} style={getDistributionStyle()}>
              {props.choices.map((choice: string) => {
                const isSelected = userChoices.has(choice)
                const distribution = props.webDistribution || props.distribution

                return (
                  <div
                    key={choice}
                    role='option'
                    aria-selected={isSelected}
                    tabIndex={0}
                    className={cn(styles.multiChoiceButton, styles[getTextLengthClass(choice)], 'lxActionButton lxActionButtonDefaultSize', isSelected && 'lxActionButtonFilled')}
                    style={getButtonStyle(isSelected, distribution)}
                    onClick={() => onChoiceSelect(choice)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onChoiceSelect(choice)
                      }
                    }}
                  >
                    <span style={isSelected ? props.selectedStyle.label : props.unselectedStyle.label}>{choice}</span>
                  </div>
                )
              })}
            </div>
          </FormControl>
        )}

        {/* 🔹 Image-type input */}
        {props.input_type === 'image' && (
          <div className={styles.multiChoiceImageGrid} style={getImageDistributionStyle()}>
            {props.choices.map((choice: string, index: number) => (
              <div
                key={index}
                role='option'
                aria-selected={userChoices.has(choice)}
                tabIndex={0}
                className={cn(styles.multiChoiceImageItem, userChoices.has(choice) && 'selected')}
                onClick={() => onChoiceSelect(choice)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onChoiceSelect(choice)
                  }
                }}
              >
                <img src={choice} alt={`Choice ${index + 1}`} loading='lazy' />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 🔹 Actions (for multi, dropdown, or optional) */}
      {(props.input_type === 'dropdown' || isMultiAnswer || data.optional) && (
        <div className={cn(styles.multiChoiceActions, 'sticky')}>
          {isOptional && (
            <LxCancelButton onCancel={() => onSubmit(true)} disabled={submitted} label={props.btnSkip} classNames={cn('lxActionButton', 'lxActionButtonDefaultSize', styles.pollButton)} />
          )}

          {canSubmit && (
            <LxSaveButton
              disabled={submitted || !canSubmit}
              onSubmit={() => onSubmit(false)}
              label={props.btnOk}
              classNames={cn('lxActionButton', 'lxActionButtonDefaultSize', 'lxActionButtonFilled', styles.pollButton)}
            />
          )}
        </div>
      )}
    </div>
  )
}
